import {
  Hue,
  HueVariant,
  StyleToken,
  Theme,
} from "ipctg-inflow-types/InFlowThemes"

import styles from "./Button.module.css"

export type ButtonType = keyof NonNullable<
  NonNullable<NonNullable<Theme["components"]>["atoms"]>["button"]
>

// Centralized mapping for button types to hues (for fallback)
const DEFAULT_BUTTON_STYLE_TOKENS: Record<ButtonType, Hue> = {
  primary: "primary",
  default: "neutral",
  danger: "danger",
  caution: "caution",
  success: "success",
  info: "info",
}

export type Presentation = "filled" | "ghost" | "link"

export type ButtonStylingProps = {
  presentation?: "filled" | "ghost" | "link"
} & (
  | { buttonType?: ButtonType; forced?: never }
  | { forced: StyleToken; buttonType?: never }
)

export function buttonClasses(args: {
  styling: ButtonStylingProps
  theme: Theme | undefined
}) {
  if (args.styling.forced) {
    return [
      hueClass(args.styling.forced.hue),
      hueVariantClass(
        args.styling.presentation || "filled",
        args.styling.forced.variant
      ),
      appearanceClass(args.styling.presentation || "filled"),
    ]
  }

  const buttonThemes = args.theme?.components?.atoms?.button
  const buttonType = args.styling.buttonType || "default"
  const token = buttonThemes?.[buttonType]

  const hue =
    token && "hue" in token
      ? token.hue
      : DEFAULT_BUTTON_STYLE_TOKENS[buttonType]
  const variant = token?.variant || "solid"
  const presentation: Presentation = args.styling.presentation || "filled"

  return [
    hueClass(hue),
    hueVariantClass(presentation, variant),
    appearanceClass(presentation),
  ]

  function hueClass(hue: Hue) {
    switch (hue) {
      case "primary":
        return styles.primary
      case "secondary":
        return styles.secondary
      case "neutral":
        return styles.neutral
      case "success":
        return styles.success
      case "caution":
        return styles.caution
      case "danger":
        return styles.danger
      default:
        console.error(`Unhandled or unexpected hue style: ${hue}.`)
        return styles.neutral
    }
  }

  function hueVariantClass(presentation: Presentation, variant: HueVariant) {
    if (presentation !== "filled") {
      return "inverse"
    }

    switch (variant) {
      case "light":
        return styles.light
      case "solid":
        return styles.solid
      case "inverse":
        return styles.inverse
      case "default":
      default:
        return styles.defaultVariant
    }
  }

  function appearanceClass(presentation: Presentation) {
    switch (presentation) {
      case "ghost":
        return styles.ghost
      case "link":
        return styles.link
    }
  }
}
